import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { createForm, createFormControl, FormControls, reverseRecord, TypedFormGroup } from 'osn-shared';
import { Observable, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { PageAccesses } from '../../../core/guards/page-accesses.model';
import { PageAccessesService } from '../../../core/services/page-access/page-access.service';
import { PageType } from '../../../page-edit-screen/page-type';
import { PageScreenService } from './page-screen.service';
import { PageScreenViewModel, TabType } from './page-screen.view-model';
import { AdminUiDetailsService } from '../../../admin-ui-details/admin-ui-details.service';
import { AdminUiDetails, AdminUiPageType, GroupedAdminUiPageActions, GroupedAdminUiPageTypes } from '../../../domain/admin-ui-details';

interface FormModel {
  contentPageFilter: string;
  redeemPageFilter: string;
  genrePageFilter: string;
  collectionPageFilter: string;
}

@Component({
  selector: 'osn-cxb-page-screen',
  templateUrl: './page-screen.component.html',
  styleUrls: ['./page-screen.component.scss'],
  providers: [PageScreenService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageScreenComponent implements OnInit, OnDestroy {
  private readonly formDebounceMs = 100;
  private readonly indexToRouteMap: Record<number, TabType> = {
    0: 'content',
    1: 'marketing',
    2: 'redeem',
    3: 'genre',
    4: 'collection',
  };
  private readonly routeToIndexMap = reverseRecord(this.indexToRouteMap);

  public readonly vm$: Observable<PageScreenViewModel> = this.pageScreenService.vm$;
  public readonly PageType = PageType;

  public readonly formControls: FormControls<FormModel> = {
    contentPageFilter: createFormControl(''),
    genrePageFilter: createFormControl(''),
    redeemPageFilter: createFormControl(''),
    collectionPageFilter: createFormControl(''),
  };

  public readonly form: TypedFormGroup<FormModel> = createForm(this.formControls);

  public activeIndex = 0;

  private readonly destroyed$ = new Subject<void>();
  public accesses: PageAccesses;
  public readonly AdminUiPageType = AdminUiPageType;
  public pageItemTypes$: Observable<GroupedAdminUiPageTypes>;

  constructor(
    private readonly pageScreenService: PageScreenService,
    private readonly pageAccessService: PageAccessesService,
    private readonly router: Router,
    private readonly adminUiDetailsService: AdminUiDetailsService
  ) {
    this.pageItemTypes$ = this.adminUiDetailsService.pageItemTypes$;
  }

  public ngOnInit() {
    this.accesses = this.pageAccessService.usersPageAccesses$.value;
    this.form.valueChanges.pipe(debounceTime(this.formDebounceMs), takeUntil(this.destroyed$)).subscribe((filters) => {
      this.pageScreenService.filtersChanged(filters);
    });

    this.vm$.pipe(takeUntil(this.destroyed$)).subscribe((vm) => (this.activeIndex = this.routeToIndexMap[vm.subRoute] ?? 0));
  }

  public ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  public onCreateNewPageClick(pageType: PageType) {
    this.pageScreenService.openCreatePageDialog(pageType);
  }

  public onTabindexChange(index: number) {
    this.router.navigate(['/pages', this.indexToRouteMap[index]]);
  }
}
